const COLLECTION_SELECTORS = {
  workspace: '.catalog_workspace',
  resultsPanelLoader: '.catalog_results_panel .loader',
  productsCount: '.collection_products_count',
  mobileDrawer: '.mobile_filter_drawer'
};

let hasBoundCollectionHistory = false;

function safeDefineElement(name, ctor) {
  if (!customElements.get(name)) {
    customElements.define(name, ctor);
  }
}

function replaceChildrenFromMarkup(markup, selector) {
  const source = document.createElement('div');
  source.innerHTML = markup;

  const incoming = source.querySelector(selector);
  const current = document.querySelector(selector);

  if (incoming && current) {
    current.replaceChildren(...incoming.childNodes);
  }
}

async function fetchSectionMarkup(url, sectionId) {
  const response = await fetch(url.toString());
  if (!response.ok) {
    throw new Error('Collection section request failed');
  }

  const payload = await response.json();
  return payload[sectionId];
}

function emitCollectionUpdate(url, sectionId) {
  document.dispatchEvent(
    new CustomEvent('collection-grid-update', {
      detail: {
        url,
        sectionId
      }
    })
  );
}

function pushCleanHistory(url) {
  const clean = new URL(url.toString());
  clean.searchParams.delete('sections');

  const current = new URL(window.location.href);
  current.searchParams.delete('sections');

  if (clean.toString() === current.toString()) {
    return;
  }

  window.history.pushState({ collectionFilters: true }, '', clean);
}

function resolveSectionId() {
  const holder = document.querySelector('[data-section-id]');
  return holder?.dataset.sectionId || null;
}

function buildSectionUrl(url, sectionId) {
  const requestUrl = new URL(url.toString(), window.location.origin);
  requestUrl.searchParams.set('sections', sectionId);
  return requestUrl;
}

function replaceCollectionMarkup(markup) {
  replaceChildrenFromMarkup(markup, COLLECTION_SELECTORS.workspace);
  replaceChildrenFromMarkup(markup, COLLECTION_SELECTORS.productsCount);
  replaceChildrenFromMarkup(markup, COLLECTION_SELECTORS.mobileDrawer);
}

async function refreshCollectionForUrl(url, sectionId, options = {}) {
  const { pushHistory = false, emitEvent = true } = options;
  const requestUrl = buildSectionUrl(url, sectionId);
  const markup = await fetchSectionMarkup(requestUrl, sectionId);

  replaceCollectionMarkup(markup);

  if (emitEvent) {
    emitCollectionUpdate(url, sectionId);
  }

  if (pushHistory) {
    pushCleanHistory(url);
  }
}

function initCollectionHistorySync() {
  if (hasBoundCollectionHistory) return;

  window.addEventListener('popstate', async () => {
    const sectionId = resolveSectionId();
    if (!sectionId) return;

    const pageUrl = new URL(window.location.href);

    try {
      await refreshCollectionForUrl(pageUrl, sectionId, {
        pushHistory: false,
        emitEvent: true
      });
    } catch (error) {
      console.error('[CollectionHistory]', error);
    }
  });

  hasBoundCollectionHistory = true;
}

class CatalogFilterPanel extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
    this.sortSelect = document.querySelector('select[name="sort_by"]');
    this.checkboxInputs = [...this.querySelectorAll('input[type="checkbox"]')];
    this.priceInputs = [
      ...this.querySelectorAll('input[data-price-range-input]'),
      ...this.querySelectorAll('input[type="number"]')
    ];
    this.activeFilterLinks = [...this.querySelectorAll('.active_values_wrapper a')];
    this.priceTimers = new Map();
  }

  connectedCallback() {
    initCollectionHistorySync();
    this.bindActiveFilterRemoval();
    this.bindCheckboxFilters();
    this.bindPriceRangeFilters();
  }

  bindActiveFilterRemoval() {
    if (!this.activeFilterLinks.length) return;

    this.activeFilterLinks.forEach((link) => {
      link.addEventListener('click', (event) => {
        event.preventDefault();
        const url = new URL(link.href, window.location.origin);
        url.searchParams.set('sections', this.sectionId);
        this.applySectionUpdate(url);
      });
    });
  }

  bindCheckboxFilters() {
    if (!this.checkboxInputs.length) return;

    this.checkboxInputs.forEach((input) => {
      input.addEventListener('change', () => {
        const base = input.checked ? input.dataset.addUrl : input.dataset.removeUrl;
        const url = new URL(base, window.location.origin);
        url.searchParams.set('sections', this.sectionId);
        this.applySectionUpdate(url);
      });
    });
  }

  bindPriceRangeFilters() {
    if (!this.priceInputs.length) return;

    this.priceInputs.forEach((input) => {
      input.addEventListener('input', () => {
        if (this.priceTimers.has(input)) {
          clearTimeout(this.priceTimers.get(input));
        }

        const timer = setTimeout(() => {
          this.priceTimers.delete(input);
          this.handlePriceInput(input);
        }, 900);

        this.priceTimers.set(input, timer);
      });
    });
  }

  handlePriceInput(changedInput) {
    const url = new URL(window.location.href);
    const minInput = this.getSiblingRangeInput(changedInput, 'gte');
    const maxInput = this.getSiblingRangeInput(changedInput, 'lte');

    this.syncRangeParam(url, minInput);
    this.syncRangeParam(url, maxInput);

    url.searchParams.set('sections', this.sectionId);
    this.applySectionUpdate(url);
  }

  getSiblingRangeInput(origin, bound) {
    const fieldName = origin.name.includes(bound)
      ? origin.name
      : origin.name.replace(bound === 'gte' ? 'lte' : 'gte', bound);

    return this.querySelector(`input[name="${fieldName}"]`);
  }

  syncRangeParam(url, input) {
    if (!input) return;

    const defaultValue = input.dataset.default;

    if (defaultValue !== undefined && input.value === defaultValue) {
      url.searchParams.delete(input.name);
      return;
    }

    if (input.value) {
      url.searchParams.set(input.name, input.value);
    } else {
      url.searchParams.delete(input.name);
    }
  }

  toggleLoading(active) {
    const loader = document.querySelector(COLLECTION_SELECTORS.resultsPanelLoader);

    if (loader) {
      loader.classList.toggle('hidden', !active);
    }

    if (this.sortSelect) {
      this.sortSelect.disabled = active;
    }
  }

  async applySectionUpdate(url) {
    this.toggleLoading(true);

    // The refresh replaces the whole workspace (including this panel and the
    // element that currently has focus), so remember what was focused and
    // restore it afterwards to preserve keyboard focus order.
    const focusId = this.getFocusRestoreId(document.activeElement);

    try {
      await refreshCollectionForUrl(url, this.sectionId, {
        pushHistory: true,
        emitEvent: true
      });
      this.restoreFocus(focusId);
    } catch (error) {
      console.error('[CatalogFilterPanel]', error);
    } finally {
      this.toggleLoading(false);
    }
  }

  getFocusRestoreId(element) {
    if (element && element !== document.body && element.id && this.contains(element)) {
      return element.id;
    }
    return null;
  }

  restoreFocus(focusId) {
    const panel =
      document.querySelector(`catalog-filters[data-section-id="${this.sectionId}"]`) ||
      document.querySelector('catalog-filters');

    if (!panel) return;

    if (focusId) {
      // Attribute selector avoids escaping the dotted filter ids.
      const target = panel.querySelector(`[id="${focusId}"]`);
      if (target) {
        target.focus({ preventScroll: true });
        return;
      }
    }

    // Fallback (e.g. an active-filter link that no longer exists): keep focus
    // inside the filters block rather than letting it fall back to <body>.
    const fallback = panel.querySelector('summary, a[href], input');
    fallback?.focus({ preventScroll: true });
  }
}

class CatalogMobileFilters extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
    this.submitButton = this.querySelector('.mobile_filters_btn button');
    this.form = this.querySelector('form#mobile_filters_form');
    this.drawer = document.querySelector(COLLECTION_SELECTORS.mobileDrawer);
    this.collectionWorkspace = document.querySelector(COLLECTION_SELECTORS.workspace);
    this.activeValues = [...this.querySelectorAll('.mob_active_values_wrapper a')];
  }

  connectedCallback() {
    initCollectionHistorySync();
    this.bindApplyButton();
    this.bindActiveValues();
  }

  bindApplyButton() {
    if (!this.submitButton) return;

    this.submitButton.addEventListener('click', async (event) => {
      event.preventDefault();
      if (!this.form || !this.collectionWorkspace) return;

      this.toggleLoading(true);
      const url = this.buildFormUrl();

      await this.refreshPage(url);
      this.closeDrawer();
      this.toggleLoading(false);
    });
  }

  bindActiveValues() {
    if (!this.activeValues.length) return;

    this.activeValues.forEach((link) => {
      link.addEventListener('click', async (event) => {
        event.preventDefault();
        this.toggleLoading(true);

        const url = new URL(link.getAttribute('href'), window.location.href);
        await this.refreshPage(url.toString());

        this.closeDrawer();
        this.toggleLoading(false);
      });
    });
  }

  buildFormUrl() {
    const formData = new FormData(this.form);
    const params = new URLSearchParams();
    const current = new URLSearchParams(window.location.search);
    const hasSortBy = formData.has('sort_by');

    const minPrice = this.form.querySelector('[name="filter.v.price.gte"]');
    const maxPrice = this.form.querySelector('[name="filter.v.price.lte"]');
    const defaultMin = minPrice?.getAttribute('data-default');
    const defaultMax = maxPrice?.getAttribute('data-default');

    if (minPrice && maxPrice && defaultMin && defaultMax) {
      if (minPrice.value === defaultMin && maxPrice.value === defaultMax) {
        minPrice.value = '';
        maxPrice.value = '';
        formData.delete('filter.v.price.gte');
        formData.delete('filter.v.price.lte');
      }
    }

    for (const [key, value] of current.entries()) {
      if (key.startsWith('filter.v.') || key.startsWith('filter.p.')) continue;
      if (key === 'sort_by' && hasSortBy) continue;
      params.append(key, value);
    }

    for (const [key, value] of formData.entries()) {
      if (!value) continue;
      if (key === 'sort_by') {
        params.set(key, value);
      } else {
        params.append(key, value);
      }
    }

    return `${window.location.pathname}?${params.toString()}`;
  }

  async refreshPage(url) {
    const pageUrl = new URL(url.toString(), window.location.origin);

    try {
      await refreshCollectionForUrl(pageUrl, this.sectionId, {
        pushHistory: true,
        emitEvent: true
      });
    } catch (error) {
      console.error('[CatalogMobileFilters]', error);
    }
  }

  toggleLoading(active) {
    const loader = document.querySelector(COLLECTION_SELECTORS.resultsPanelLoader);
    const sortField = document.querySelector('select[name="sort_by"]');

    if (loader) {
      loader.classList.toggle('hidden', !active);
    }

    if (sortField) {
      sortField.disabled = active;
    }
  }

  closeDrawer() {
    const drawerHost = this.closest('global-drawer') || document.getElementById('mobile_filters');

    if (drawerHost && typeof drawerHost.close === 'function') {
      drawerHost.close(false, { restoreFocus: false });
      return;
    }

    if (this.drawer) {
      this.drawer.classList.remove('closing', 'active');
      this.drawer.setAttribute('aria-hidden', 'true');
      this.drawer.setAttribute('inert', '');
    }

    document.body.classList.remove('overflow-hidden');
    document.body.style.overflow = '';
    window.whenLenisReady(lenis => lenis.start());
  }
}

class CatalogSorter extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
    this.select = this.querySelector('select[name="sort_by"]');
  }

  connectedCallback() {
    initCollectionHistorySync();
    if (!this.select || !this.sectionId) return;

    this.select.addEventListener('change', () => {
      this.handleSortChange(this.select.value);
    });
  }

  async handleSortChange(sortValue) {
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', sortValue);
    url.searchParams.set('sections', this.sectionId);

    this.toggleLoading(true);

    try {
      await refreshCollectionForUrl(url, this.sectionId, {
        pushHistory: true,
        emitEvent: true
      });
    } catch (error) {
      console.error('[CatalogSorter]', error);
    } finally {
      this.toggleLoading(false);
    }
  }

  toggleLoading(active) {
    const loader = document.querySelector(COLLECTION_SELECTORS.resultsPanelLoader);

    if (loader) {
      loader.classList.toggle('hidden', !active);
    }

    if (this.select) {
      this.select.disabled = active;
    }
  }
}

class PriceFilterRange extends HTMLElement {
  constructor() {
    super();
    this.minInput = this.querySelector('input[data-min-value]');
    this.maxInput = this.querySelector('input[data-max-value]');
    this.minDisplay = this.querySelector('[data-min-display]');
    this.maxDisplay = this.querySelector('[data-max-display]');
    this.track = this.querySelector('.slider_track');
    this.symbol = this.minInput?.dataset.symbol || this.maxInput?.dataset.symbol || '';
  }

  connectedCallback() {
    if (!this.minInput || !this.maxInput) return;

    this.minInput.addEventListener('input', () => this.handleInput('min'));
    this.maxInput.addEventListener('input', () => this.handleInput('max'));
    this.render();
  }

  handleInput(source) {
    const minValue = Number(this.minInput.value || 0);
    const maxValue = Number(this.maxInput.value || 0);

    if (source === 'min' && minValue > maxValue) {
      this.maxInput.value = String(minValue);
    }

    if (source === 'max' && maxValue < minValue) {
      this.minInput.value = String(maxValue);
    }

    this.render();
  }

  render() {
    const lowerBound = Number(this.minInput.min || 0);
    const upperBound = Number(this.minInput.max || 0);
    const minValue = Number(this.minInput.value || 0);
    const maxValue = Number(this.maxInput.value || 0);
    const range = Math.max(upperBound - lowerBound, 1);
    const start = ((minValue - lowerBound) / range) * 100;
    const end = ((maxValue - lowerBound) / range) * 100;

    if (this.minDisplay) {
      this.minDisplay.textContent = `${this.symbol}${Math.round(minValue)}`;
    }

    if (this.maxDisplay) {
      this.maxDisplay.textContent = `${this.symbol}${Math.round(maxValue)}`;
    }

    if (this.track) {
      this.track.style.setProperty('--range-start', `${start}%`);
      this.track.style.setProperty('--range-end', `${end}%`);
    }
  }
}

class SearchResetButton extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('.button_reset');
  }

  connectedCallback() {
    if (!this.button) return;

    this.button.addEventListener('click', (event) => {
      event.preventDefault();
      const url = new URL(window.location.href);
      url.search = '';
      window.location.href = url.toString();
    });
  }
}

safeDefineElement('catalog-sorter', CatalogSorter);
safeDefineElement('catalog-filters', CatalogFilterPanel);
safeDefineElement('catalog-mobile-filters', CatalogMobileFilters);
safeDefineElement('price-filter-range', PriceFilterRange);
safeDefineElement('search-reset-button', SearchResetButton);
